import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  ScheduleConfig,
  ScheduleEvent,
  ScheduleCallbacks,
  ViewType,
  HostToEmbeddedMessage,
  EmbeddedToHostMessage,
  DateRange,
  ConnectionStatus,
  PropertyItem,
  AiCreateEventRequest,
} from '../types';
import type { ScreenerGateConfig, ScreenerState, PendingSlot, ActiveScreener, CrisisRouteData, ScreenerCompletePayload, ScreenerResultPayload } from '../screener/screenerTypes';
import { useScreenerGate } from '../screener/useScreenerGate';
import { ScreenerGateDialogMount } from '../screener/ScreenerGateDialog';
import type { AcuityReservation, AcuityMachineState, AcuitySlotClaimedPayload } from '../acuity/acuityTypes';
import { useAcuitySlots } from '../acuity/useAcuitySlots';
import type { ProviderMatchingConfig, PatientContext, MatchingResultsPayload } from '../matching/matchingTypes';
import { useProviderMatching } from '../matching/useProviderMatching';
import type { ProviderMatchingHandle } from '../matching/matchingTypes';
import type {
  WaitlistConfig,
  WaitlistHandle,
  WaitlistStatePayload,
  BackfillMatchPayload,
  BackfillNoMatchPayload,
  BackfillOutcomePayload,
  SlotOpenedPayload,
} from '../waitlist/waitlistTypes';
import { useWaitlist } from '../waitlist/useWaitlist';
import type { NoShowRiskConfig, NoShowRiskHandle, RiskScoresPayload, ReminderOutcomePayload, AppointmentStatusUpdatePayload } from '../risk/riskTypes';
import { useNoShowRisk } from '../risk/useNoShowRisk';
import type { GroupTherapyConfig, GroupTherapyHandle, GroupSessionsStatePayload, GroupEnrollOutcomePayload, GroupAttendancePayload, GroupConflictResultPayload } from '../group/groupTypes';
import { useGroupTherapy } from '../group/useGroupTherapy';
import { GroupEnrollDialogMount } from '../group/GroupEnrollDialog';
import { toZonedTime } from 'date-fns-tz';
import { getViewRange, resolveTimezone, expandHolidays } from '../utils/dateUtils';

// ─── Context shape ─────────────────────────────────────────────────────────

interface ScheduleContextValue {
  config: ScheduleConfig;
  events: ScheduleEvent[];
  currentDate: Date;
  setCurrentDate: (d: Date) => void;
  setView: (v: ViewType) => void;
  setTimezone: (tz: string) => void;
  viewRange: DateRange;
  callbacks: ScheduleCallbacks;
  emit: (msg: EmbeddedToHostMessage) => void;
  /** Exposed so custom views (MonthMondays) can trigger delete without Kendo */
  handleDeleteEvent: (id: string) => void;
  /** Exposed so custom views can push edits back */
  handleSaveEvent: (event: ScheduleEvent) => void;
  handleCreateEvent: (event: Omit<ScheduleEvent, 'id'>) => void;
  /** Called by KendoSchedulerView when it updates local events (keeps parent in sync) */
  setEvents: React.Dispatch<React.SetStateAction<ScheduleEvent[]>>;
  /** Read-only all-day holiday events derived from config.holidays. Never go through callbacks. */
  holidayEvents: ScheduleEvent[];
  /** Current resource page index (0-based) shared between ResourcePanel and KendoSchedulerView */
  resourcePage: number;
  setResourcePage: React.Dispatch<React.SetStateAction<number>>;
  /** SSE / live-update connection status surfaced by the host. Drives the toolbar indicator. */
  connectionStatus: ConnectionStatus | undefined;
  setSlotDuration: (minutes: number) => void;
  /** Events flagged with queued:true — shown in the scheduling queue panel, hidden from the grid. */
  queuedEvents: ScheduleEvent[];
  /** Ref holding the id of the queued event currently being dragged (null when idle). */
  queueDragRef: React.MutableRefObject<string | null>;
  /** Places a queued event on the schedule grid at the given slot coordinates. */
  handleScheduleQueuedEvent: (eventId: string, newStartUtc: string, newEndUtc: string, resourceId?: string) => void;
  /** The event the user last clicked (when propertiesPanel is configured). */
  selectedEvent: ScheduleEvent | null;
  /** Key/value pairs returned by callbacks.onEventClick for the selected event. */
  eventProperties: PropertyItem[];
  /** Called by the item renderer when the user clicks an event with propertiesPanel configured. */
  handleEventClick: (event: ScheduleEvent) => void;
  /** Called on double-click. Returns true if the host wants to suppress the default edit form. */
  handleEventDoubleClick: (event: ScheduleEvent) => boolean;
  /** Called by SchedulerFormWrapper when a new slot is clicked and doubleClickForm === 'host'. */
  handleNewEventDoubleClick: (startUtc: string, endUtc: string, resourceId?: string) => void;
  /** Set to true by handleEventDoubleClick when the host suppresses the form; cleared by SchedulerFormWrapper. */
  suppressNextFormRef: React.MutableRefObject<boolean>;
  /** Set by AiChatPanel when the AI response contains a createEvent request. Cleared by the scheduler view after opening the form. */
  pendingNewEventRequest: AiCreateEventRequest | null;
  setPendingNewEventRequest: (req: AiCreateEventRequest | null) => void;
  // ── Screener Gate ────────────────────────────────────────────────────────
  screenerGate: ScreenerGateConfig | undefined;
  screenerState: ScreenerState;
  screenerPendingSlot: PendingSlot | null;
  screenerActiveScreener: ActiveScreener | null;
  screenerCrisisData: CrisisRouteData | null;
  /** Called by SchedulerFormWrapper when screenerGate.enabled and a new slot is clicked. */
  handleSlotIntercepted: (slot: PendingSlot) => void;
  screenerOnCompleted: (responses: { questionId: string; value: number | string }[]) => ScreenerCompletePayload | null;
  screenerOnCrisisAcknowledged: () => void;
  screenerReset: () => void;
  // ── Acuity Slot Reservation ────────────────────────────────────────────────
  acuityReservations: AcuityReservation[];
  acuityMachineState: AcuityMachineState;
  /** Called by AcuitySlotRenderer when a reserved slot is clicked in privileged mode. */
  handleAcuitySlotClaimed: (reservation: AcuityReservation) => void;
  /** Called by AiChatPanel after onAcuityCommand resolves in direct-React mode. */
  applyAcuityConfig: (reservations: AcuityReservation[]) => void;
  privilegedView: boolean;
  // ── Provider Matching ──────────────────────────────────────────────────────
  providerMatching: ProviderMatchingConfig | undefined;
  patientContext: PatientContext | undefined;
  matchingHandle: ProviderMatchingHandle;
  // ── Waitlist + Auto-Backfill ───────────────────────────────────────────────
  waitlistHandle: WaitlistHandle;
  // ── No-Show Risk + Smart Reminder Engine ──────────────────────────────────
  noShowRiskHandle: NoShowRiskHandle;
  // ── Group Therapy ──────────────────────────────────────────────────────────
  groupTherapyHandle: GroupTherapyHandle;
}

const ScheduleContext = createContext<ScheduleContextValue | null>(null);

export function useSchedule(): ScheduleContextValue {
  const ctx = useContext(ScheduleContext);
  if (!ctx) throw new Error('useSchedule must be used inside ScheduleProvider');
  return ctx;
}

// ─── Provider ──────────────────────────────────────────────────────────────

interface ProviderProps {
  config: ScheduleConfig;
  events: ScheduleEvent[];
  callbacks: ScheduleCallbacks;
  iframeMode?: boolean;
  connectionStatus?: ConnectionStatus;
  screenerGate?: ScreenerGateConfig;
  privilegedView?: boolean;
  providerMatching?: ProviderMatchingConfig;
  patientContext?: PatientContext;
  waitlist?: WaitlistConfig;
  noShowRisk?: NoShowRiskConfig;
  groupTherapy?: GroupTherapyConfig;
  children: React.ReactNode;
}

export function ScheduleProvider({
  config: configProp,
  events: eventsProp,
  callbacks,
  iframeMode = false,
  connectionStatus: connectionStatusProp,
  screenerGate,
  privilegedView = false,
  providerMatching,
  patientContext,
  waitlist,
  noShowRisk,
  groupTherapy,
  children,
}: ProviderProps) {
  const [config, setConfig] = useState<ScheduleConfig>(configProp);
  const [events, setEvents] = useState<ScheduleEvent[]>(eventsProp);
  const [selectedEvent, setSelectedEvent] = useState<ScheduleEvent | null>(null);
  const [eventProperties, setEventProperties] = useState<PropertyItem[]>([]);
  const [pendingNewEventRequest, setPendingNewEventRequest] = useState<AiCreateEventRequest | null>(null);
  const queueDragRef = useRef<string | null>(null);
  const [currentDate, setCurrentDate] = useState<Date>(
    configProp.referenceDate ? new Date(configProp.referenceDate) : new Date(),
  );
  const [resourcePage, setResourcePage] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | undefined>(connectionStatusProp);
  useEffect(() => { setConnectionStatus(connectionStatusProp); }, [connectionStatusProp]);

  // TV mode: derive an effective config that applies TV overrides transparently to all consumers.
  const effectiveConfig = useMemo((): ScheduleConfig => {
    const tv = config.tvMode;
    if (!tv?.enabled) return config;
    const allResources = config.resources ?? [];
    const tvFiltered = tv.tvResources?.length
      ? allResources.filter((r) => tv.tvResources!.includes(r.id))
      : allResources;
    return {
      ...config,
      view: 'day',
      showFullDay: true,
      resources: tvFiltered,
      resourcesPerPage: tv.resourcesPerPage ?? 4,
      resourceGrouping: 'byDate',
      allowEventCreation: false,
    };
  }, [config]);

  // Sync prop changes — merge rather than replace so that internal view state
  // (set by the Toolbar's view buttons) is not overwritten when the host changes
  // an unrelated config field (e.g. headerDateFormats, timezone, …).
  // The view is only taken from the incoming prop when the host explicitly
  // changed it (i.e. configProp.view differs from the previous configProp.view).
  const prevConfigPropRef = useRef(configProp);
  useEffect(() => {
    const prev = prevConfigPropRef.current;
    prevConfigPropRef.current = configProp;
    setConfig((c) => ({
      ...configProp,
      view: configProp.view !== prev.view ? configProp.view : c.view,
      timezone: configProp.timezone !== prev.timezone ? configProp.timezone : c.timezone,
    }));
  }, [configProp]);
  useEffect(() => { setEvents(eventsProp); }, [eventsProp]);

  // Reset to page 0 when the resource list, grouping mode, or view changes.
  useEffect(() => { setResourcePage(0); }, [config.resources?.length, config.resourceGrouping, config.view]);

  // postMessage emit helper
  const emit = useCallback(
    (msg: EmbeddedToHostMessage) => {
      if (iframeMode) window.parent.postMessage(msg, '*');
    },
    [iframeMode],
  );

  // Refs for screener gate message handlers — populated after useScreenerGate is called below,
  // but captured here so the postMessage useEffect can reference them without a TDZ issue.
  const screenerOnConfigReceivedRef = useRef<((s: ActiveScreener) => void) | null>(null);
  const screenerOnSkipRef = useRef<(() => void) | null>(null);
  const screenerOnResultReceivedRef = useRef<((r: ScreenerResultPayload) => void) | null>(null);

  // Refs for acuity slot message handlers — same TDZ-avoidance pattern.
  const acuityOnConfigReceivedRef = useRef<((r: AcuityReservation[]) => void) | null>(null);
  const acuityOnReleaseConfirmRef = useRef<((id: string) => void) | null>(null);
  const acuityOnReleaseHoldRef = useRef<((id: string, newReleaseAt?: string) => void) | null>(null);

  // Ref for provider matching message handler.
  const matchingApplyResultsRef = useRef<((r: MatchingResultsPayload) => void) | null>(null);
  const matchingApplyContextRef = useRef<((ctx: PatientContext) => void) | null>(null);

  // Refs for waitlist message handlers — populated after useWaitlist is called below.
  const waitlistOnStateRef = useRef<((p: WaitlistStatePayload) => void) | null>(null);
  const waitlistOnMatchRef = useRef<((p: BackfillMatchPayload) => void) | null>(null);
  const waitlistOnNoMatchRef = useRef<((p: BackfillNoMatchPayload) => void) | null>(null);
  const waitlistOnOutcomeRef = useRef<((p: BackfillOutcomePayload) => void) | null>(null);
  const waitlistOnScheduleUpdatedRef = useRef<(() => void) | null>(null);
  // Used by handleDeleteEvent (defined below) to fire slot-opened without a circular dep.
  const waitlistHandleRef = useRef<WaitlistHandle | null>(null);
  // Used by handleDeleteEvent to read risk scores and match data without closure issues.
  const noShowRiskHandleRef = useRef<NoShowRiskHandle | null>(null);
  const matchingHandleForDeleteRef = useRef<ProviderMatchingHandle | null>(null);

  // Refs for no-show risk message handlers.
  const riskOnScoresRef = useRef<((p: RiskScoresPayload) => void) | null>(null);
  const riskOnOutcomeRef = useRef<((p: ReminderOutcomePayload) => void) | null>(null);
  const riskOnStatusUpdateRef = useRef<((p: AppointmentStatusUpdatePayload) => void) | null>(null);

  // Refs for group therapy message handlers.
  const groupOnSessionsRef = useRef<((p: GroupSessionsStatePayload) => void) | null>(null);
  const groupOnEnrollOutcomeRef = useRef<((p: GroupEnrollOutcomePayload) => void) | null>(null);
  const groupOnAttendanceRef = useRef<((p: GroupAttendancePayload) => void) | null>(null);
  const groupOnConflictResultRef = useRef<((p: GroupConflictResultPayload) => void) | null>(null);

  // postMessage listener (host → embedded)
  useEffect(() => {
    if (!iframeMode) return;
    const handler = (e: MessageEvent) => {
      const msg = e.data as HostToEmbeddedMessage;
      if (!msg?.type) return;
      switch (msg.type) {
        case 'SET_CONFIG':   setConfig(msg.payload); break;
        case 'SET_EVENTS':   setEvents(msg.payload); break;
        case 'ADD_EVENTS':
          setEvents((prev) => [
            ...prev.filter((ev) => !msg.payload.find((n) => n.id === ev.id)),
            ...msg.payload,
          ]);
          break;
        case 'UPDATE_EVENT':
          setEvents((prev) => prev.map((ev) => ev.id === msg.payload.id ? msg.payload : ev));
          break;
        case 'REMOVE_EVENT':
          setEvents((prev) => prev.filter((ev) => ev.id !== msg.payload.id));
          break;
        case 'NAVIGATE_TO':  setCurrentDate(new Date(msg.payload.date)); break;
        case 'SET_VIEW':     setConfig((c) => ({ ...c, view: msg.payload.view })); break;
        case 'SET_CONNECTION_STATUS': setConnectionStatus(msg.payload.status); break;
        case 'SET_EVENT_PROPERTIES':
          setEventProperties(msg.payload.properties);
          break;
        // ── Screener Gate — delegated to refs so the effect doesn't need screenerActions ──
        case 'SCREENER_CONFIG': screenerOnConfigReceivedRef.current?.(msg.payload.screener); break;
        case 'SCREENER_SKIP':   screenerOnSkipRef.current?.();                                break;
        case 'SCREENER_RESULT': screenerOnResultReceivedRef.current?.(msg.payload);           break;
        // ── Acuity Slot Reservation ────────────────────────────────────────
        case 'ACUITY_SLOTS_CONFIG': acuityOnConfigReceivedRef.current?.(msg.payload.reservations); break;
        case 'RELEASE_CONFIRM':     acuityOnReleaseConfirmRef.current?.(msg.payload.reservationId); break;
        case 'RELEASE_HOLD':        acuityOnReleaseHoldRef.current?.(msg.payload.reservationId, msg.payload.newReleaseAt); break;
        // ── Provider Matching ────────────────────────────────────────────────
        case 'MATCHING_RESULTS':        matchingApplyResultsRef.current?.(msg.payload); break;
        case 'PATIENT_CONTEXT_UPDATE':  matchingApplyContextRef.current?.(msg.payload.patientContext); break;
        // ── Waitlist + Auto-Backfill ───────────────────────────────────────
        case 'WAITLIST_STATE':   waitlistOnStateRef.current?.(msg.payload); break;
        case 'BACKFILL_MATCH':   waitlistOnMatchRef.current?.(msg.payload); break;
        case 'BACKFILL_NO_MATCH': waitlistOnNoMatchRef.current?.(msg.payload); break;
        case 'BACKFILL_OUTCOME': waitlistOnOutcomeRef.current?.(msg.payload); break;
        case 'SCHEDULE_UPDATED': waitlistOnScheduleUpdatedRef.current?.(); break;
        // ── No-Show Risk + Smart Reminder Engine ───────────────────────────
        case 'RISK_SCORES':               riskOnScoresRef.current?.(msg.payload); break;
        case 'REMINDER_OUTCOME':          riskOnOutcomeRef.current?.(msg.payload); break;
        case 'APPOINTMENT_STATUS_UPDATE': riskOnStatusUpdateRef.current?.(msg.payload); break;
        // ── Group Therapy ──────────────────────────────────────────────────
        case 'GROUP_SESSIONS_STATE':  groupOnSessionsRef.current?.(msg.payload); break;
        case 'GROUP_ENROLL_OUTCOME':  groupOnEnrollOutcomeRef.current?.(msg.payload); break;
        case 'GROUP_ATTENDANCE':      groupOnAttendanceRef.current?.(msg.payload); break;
        case 'GROUP_CONFLICT_RESULT': groupOnConflictResultRef.current?.(msg.payload); break;
      }
    };
    window.addEventListener('message', handler);
    emit({ type: 'READY' });
    return () => window.removeEventListener('message', handler);
  }, [iframeMode, emit]);

  const setView = useCallback((v: ViewType) => {
    setConfig((c) => ({ ...c, view: v }));
  }, []);

  const setTimezone = useCallback((tz: string) => {
    setConfig((c) => ({ ...c, timezone: tz }));
  }, []);

  const setSlotDuration = useCallback((minutes: number) => {
    setConfig((c) => ({ ...c, slotDuration: minutes }));
  }, []);

  // TV mode: reset to today and page 0 whenever TV mode is first enabled.
  const prevTvEnabledRef = useRef(config.tvMode?.enabled ?? false);
  useEffect(() => {
    const enabled = config.tvMode?.enabled ?? false;
    if (enabled && !prevTvEnabledRef.current) {
      setCurrentDate(new Date());
      setResourcePage(0);
    }
    prevTvEnabledRef.current = enabled;
  }, [config.tvMode?.enabled]);

  // TV mode: advance to the next day at local midnight in the configured timezone.
  // Uses a self-rescheduling setTimeout so it fires once at midnight, resets the date,
  // then immediately schedules itself for the following midnight.
  useEffect(() => {
    if (!config.tvMode?.enabled) return;
    const tz = resolveTimezone(effectiveConfig.timezone, effectiveConfig.observeDaylightSaving);

    let timerId: ReturnType<typeof setTimeout>;

    function scheduleNextMidnight() {
      const nowInTz = toZonedTime(new Date(), tz);
      const msElapsedToday =
        ((nowInTz.getHours() * 60 + nowInTz.getMinutes()) * 60 + nowInTz.getSeconds()) * 1000 +
        nowInTz.getMilliseconds();
      const msUntilMidnight = 24 * 60 * 60 * 1000 - msElapsedToday;
      timerId = setTimeout(() => {
        setCurrentDate(new Date());
        setResourcePage(0);
        scheduleNextMidnight();
      }, msUntilMidnight);
    }

    scheduleNextMidnight();
    return () => clearTimeout(timerId);
  }, [config.tvMode?.enabled, effectiveConfig.timezone, effectiveConfig.observeDaylightSaving]);

  // TV mode: auto-advance the resource page on a configurable timer.
  useEffect(() => {
    const tv = config.tvMode;
    if (!tv?.enabled) return;
    const tvResources = effectiveConfig.resources ?? [];
    const perPage = typeof effectiveConfig.resourcesPerPage === 'number'
      ? effectiveConfig.resourcesPerPage : 4;
    const totalPages = Math.ceil(tvResources.length / perPage);
    if (totalPages <= 1) return;
    const ms = tv.autoRotateMs ?? 15000;
    const id = setInterval(() => {
      setResourcePage((p) => (p + 1) % totalPages);
    }, ms);
    return () => clearInterval(id);
  // effectiveConfig is derived from config, so listing config as dep is sufficient.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.tvMode?.enabled, config.tvMode?.autoRotateMs, effectiveConfig.resources, effectiveConfig.resourcesPerPage]);

  // TV mode: emit TV_PAGE_CHANGED whenever the page advances (or TV mode activates).
  useEffect(() => {
    if (!config.tvMode?.enabled) return;
    const tvResources = effectiveConfig.resources ?? [];
    const perPage = typeof effectiveConfig.resourcesPerPage === 'number'
      ? effectiveConfig.resourcesPerPage : 4;
    const totalPages = Math.ceil(tvResources.length / perPage);
    const pageResources = tvResources.slice(resourcePage * perPage, (resourcePage + 1) * perPage);
    const payload = { page: resourcePage, totalPages, resourceIds: pageResources.map((r) => r.id) };
    callbacks.onTvPageChanged?.(payload);
    emit({ type: 'TV_PAGE_CHANGED', payload });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourcePage, config.tvMode?.enabled]);

  const viewRange = getViewRange(effectiveConfig.view, currentDate, resolveTimezone(effectiveConfig.timezone, effectiveConfig.observeDaylightSaving), effectiveConfig.agendaDays);

  // When byDate pagination is active, compute the IDs of the resources on the
  // current page so that onRequestData can be scoped to just those resources,
  // allowing the host to fetch events lazily instead of all-at-once.
  const visibleResourceIds = useMemo((): string[] | undefined => {
    const perPage =
      typeof effectiveConfig.resourcesPerPage === 'number'
        ? effectiveConfig.resourcesPerPage
        : (effectiveConfig.resourcesPerPage?.[effectiveConfig.view] ?? 2);
    const paginationActive =
      effectiveConfig.resourceGrouping !== 'byResource' &&
      (effectiveConfig.resources?.length ?? 0) > perPage;
    if (!paginationActive || !effectiveConfig.resources?.length) return undefined;
    return effectiveConfig.resources
      .slice(resourcePage * perPage, (resourcePage + 1) * perPage)
      .map((r) => r.id);
  }, [effectiveConfig.resourcesPerPage, effectiveConfig.view, effectiveConfig.resourceGrouping, effectiveConfig.resources, resourcePage]);

  const holidayEvents = useMemo(() => {
    if (!effectiveConfig.holidays?.length) return [];
    return expandHolidays(
      effectiveConfig.holidays,
      viewRange,
      resolveTimezone(effectiveConfig.timezone, effectiveConfig.observeDaylightSaving),
    );
  // viewRange object identity changes every render; use its string values for stability.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveConfig.holidays, viewRange.startUtc, viewRange.endUtc, effectiveConfig.timezone, effectiveConfig.observeDaylightSaving]);

  // Request data whenever the view range OR (when byDate pagination is active)
  // the visible resource page changes, so each page fetches only its own events.
  const prevRangeKey = useRef('');
  useEffect(() => {
    const resourceSuffix = visibleResourceIds ? visibleResourceIds.join(',') : 'all';
    const key = `${viewRange.startUtc}|${viewRange.endUtc}|${resourceSuffix}`;
    if (prevRangeKey.current === key) return;
    prevRangeKey.current = key;
    const range: DateRange = visibleResourceIds?.length
      ? { ...viewRange, resourceIds: visibleResourceIds }
      : viewRange;
    callbacks.onRequestData(range);
    emit({ type: 'REQUEST_DATA', payload: range });
  }, [viewRange, visibleResourceIds, callbacks, emit]);

  const handleDeleteEvent = useCallback(
    (id: string) => {
      let deletedEvent: ScheduleEvent | undefined;
      setEvents((prev) => {
        deletedEvent = prev.find((e) => e.id === id || e.originalId === id);
        return prev.filter((e) => e.id !== id && e.originalId !== id);
      });
      callbacks.onEventDeleted?.(id);
      emit({ type: 'EVENT_DELETED', payload: { eventId: id } });

      // Notify waitlist: a cancelled appointment may open a backfill opportunity.
      // Phase 5: enrich payload with riskLevel + matchLevel.
      if (deletedEvent && waitlistHandleRef.current) {
        const vr = viewRange;
        const durationMs = new Date(deletedEvent.endUtc).getTime() - new Date(deletedEvent.startUtc).getTime();
        const riskScore = noShowRiskHandleRef.current?.riskScoresMap.get(deletedEvent.id);
        const matchLevel = matchingHandleForDeleteRef.current?.matches
          ?.find((m) => m.resourceId === deletedEvent!.resourceId)?.matchLevel;
        const payload: SlotOpenedPayload = {
          slotId: crypto.randomUUID(),
          resourceId: deletedEvent.resourceId ?? '',
          start: deletedEvent.startUtc,
          end: deletedEvent.endUtc,
          duration: Math.round(durationMs / 60000),
          openedBy: 'cancellation',
          previousAppointmentId: deletedEvent.id,
          screenerSeverity: deletedEvent.screenerSeverity as SlotOpenedPayload['screenerSeverity'],
          visibleRangeStart: vr.startUtc,
          visibleRangeEnd: vr.endUtc,
          riskLevel: riskScore?.riskLevel,
          matchLevel,
        };
        waitlistHandleRef.current.handleSlotOpened(payload);
      }
    },
    // viewRange object identity changes every render — exclude from dep array; read via closure is safe here
    // because viewRange is stable within any single user action (delete happens synchronously).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [callbacks, emit],
  );

  const handleSaveEvent = useCallback(
    (event: ScheduleEvent) => {
      setEvents((prev) =>
        prev.some((e) => e.id === event.id)
          ? prev.map((e) => (e.id === event.id ? event : e))
          : [...prev, event],
      );
      callbacks.onEventSaved?.(event);
      emit({ type: 'EVENT_SAVED', payload: event });
    },
    [callbacks, emit],
  );

  const handleCreateEvent = useCallback(
    (event: Omit<ScheduleEvent, 'id'>) => {
      const id = crypto.randomUUID();
      const withId: ScheduleEvent = { ...event, id };
      setEvents((prev) => [...prev, withId]);
      callbacks.onEventCreated?.(withId);
      emit({ type: 'EVENT_CREATED', payload: withId });
    },
    [callbacks, emit],
  );

  // Screener gate state machine — after emit and handleCreateEvent, both of which it depends on.
  const screenerActions = useScreenerGate(screenerGate, emit, handleCreateEvent);

  // Keep refs in sync so the postMessage handler above always calls the latest stable callbacks.
  screenerOnConfigReceivedRef.current = screenerActions.onConfigReceived;
  screenerOnSkipRef.current = screenerActions.onSkip;
  screenerOnResultReceivedRef.current = screenerActions.onResultReceived;

  // Acuity slot state machine — after emit, which it depends on.
  const acuitySlots = useAcuitySlots(
    effectiveConfig.acuitySlots,
    emit,
    viewRange,
    visibleResourceIds,
    (payload) => waitlistHandleRef.current?.handleSlotOpened(payload),
  );

  // Keep acuity refs in sync.
  acuityOnConfigReceivedRef.current = acuitySlots.onConfigReceived;
  acuityOnReleaseConfirmRef.current = acuitySlots.onReleaseConfirm;
  acuityOnReleaseHoldRef.current = acuitySlots.onReleaseHold;

  // All resource IDs in the current config (matching needs the full list, not just the page).
  const allResourceIds = useMemo(
    () => (effectiveConfig.resources ?? []).map((r) => r.id),
    [effectiveConfig.resources],
  );

  // Provider matching state machine — patientContext is tracked internally by the hook.
  const [livePatientContext, setLivePatientContext] = useState<PatientContext | undefined>(patientContext);
  // Keep livePatientContext in sync with prop changes.
  useEffect(() => { setLivePatientContext(patientContext); }, [patientContext]);

  const matchingHandle = useProviderMatching(
    providerMatching,
    livePatientContext,
    emit,
    viewRange,
    allResourceIds,
  );

  // Keep matching refs in sync so the postMessage handler above always calls the latest.
  matchingApplyResultsRef.current = matchingHandle.applyMatchingResults;
  // When PATIENT_CONTEXT_UPDATE arrives, update livePatientContext — the hook will
  // detect the change and fire a new MATCHING_REQUEST automatically.
  matchingApplyContextRef.current = setLivePatientContext;

  // Waitlist + auto-backfill state machine.
  const waitlistHandle = useWaitlist(waitlist, emit, viewRange, visibleResourceIds);

  // Keep waitlist handle ref and message-handler refs current.
  waitlistHandleRef.current = waitlistHandle;
  waitlistOnStateRef.current = waitlistHandle.onWaitlistStateReceived;
  waitlistOnMatchRef.current = waitlistHandle.onBackfillMatchReceived;
  waitlistOnNoMatchRef.current = waitlistHandle.onBackfillNoMatchReceived;
  waitlistOnOutcomeRef.current = waitlistHandle.onBackfillOutcomeReceived;
  waitlistOnScheduleUpdatedRef.current = waitlistHandle.onScheduleUpdatedReceived;

  // No-show risk + smart reminder engine.
  const noShowRiskHandle = useNoShowRisk(
    noShowRisk,
    emit,
    viewRange,
    events,
    matchingHandle,
    (payload) => waitlistHandleRef.current?.handleSlotOpened(payload),
  );

  // Keep risk refs in sync.
  noShowRiskHandleRef.current = noShowRiskHandle;
  matchingHandleForDeleteRef.current = matchingHandle;
  riskOnScoresRef.current = noShowRiskHandle.onRiskScoresReceived;
  riskOnOutcomeRef.current = noShowRiskHandle.onReminderOutcomeReceived;
  riskOnStatusUpdateRef.current = noShowRiskHandle.onAppointmentStatusUpdateReceived;

  // Group therapy state machine.
  const groupTherapyHandle = useGroupTherapy(
    groupTherapy,
    emit,
    viewRange,
    events,
    visibleResourceIds,
  );

  // Keep group therapy refs in sync.
  groupOnSessionsRef.current = groupTherapyHandle.onGroupSessionsReceived;
  groupOnEnrollOutcomeRef.current = groupTherapyHandle.onGroupEnrollOutcomeReceived;
  groupOnAttendanceRef.current = groupTherapyHandle.onGroupAttendanceReceived;
  groupOnConflictResultRef.current = groupTherapyHandle.onGroupConflictResultReceived;

  const applyAcuityConfig = useCallback(
    (reservations: AcuityReservation[]) => {
      acuitySlots.onConfigReceived(reservations);
    },
    [acuitySlots],
  );

  const handleAcuitySlotClaimed = useCallback(
    (reservation: AcuityReservation) => {
      const payload: AcuitySlotClaimedPayload = {
        reservationId: reservation.id,
        slotId: crypto.randomUUID(),
        resourceId: reservation.resourceId,
        patientId: '',
        start: reservation.start,
        end: reservation.end,
        acuityLevel: reservation.acuityLevel,
      };
      effectiveConfig.acuitySlots?.onRequestAcuitySlots({
        visibleRangeStart: viewRange.startUtc,
        visibleRangeEnd: viewRange.endUtc,
        resourceIds: visibleResourceIds ?? [],
      });
      emit({ type: 'ACUITY_SLOT_CLAIMED', payload });
    },
    [effectiveConfig.acuitySlots, emit, viewRange, visibleResourceIds],
  );

  const handleEventClick = useCallback(
    (event: ScheduleEvent) => {
      setSelectedEvent(event);
      if (callbacks.onEventClick) {
        setEventProperties(callbacks.onEventClick(event) ?? []);
      } else {
        setEventProperties([]);
      }
      emit({ type: 'EVENT_CLICKED', payload: { eventId: event.id } });
    },
    [callbacks, emit],
  );

  // Notify the host whenever the effective timezone changes (toolbar picker or prop update).
  // Use a ref to skip the first render so we don't fire spuriously on mount.
  const timezoneInitialRef = useRef(true);
  useEffect(() => {
    if (timezoneInitialRef.current) { timezoneInitialRef.current = false; return; }
    emit({ type: 'TIMEZONE_CHANGED', payload: { timezone: effectiveConfig.timezone } });
    callbacks.onTimezoneChange?.(effectiveConfig.timezone);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveConfig.timezone]);

  const suppressNextFormRef = useRef(false);

  const handleEventDoubleClick = useCallback(
    (event: ScheduleEvent): boolean => {
      emit({ type: 'EVENT_DOUBLE_CLICKED', payload: { eventId: event.id, event } });
      const configSuppresses = effectiveConfig.doubleClickForm === 'host';
      const callbackSuppresses = callbacks.onEventDoubleClick?.(event) === true;
      const suppress = configSuppresses || callbackSuppresses;
      suppressNextFormRef.current = suppress;
      return suppress;
    },
    [callbacks, emit, effectiveConfig.doubleClickForm],
  );

  const handleNewEventDoubleClick = useCallback(
    (startUtc: string, endUtc: string, resourceId?: string) => {
      emit({ type: 'NEW_EVENT_INTERCEPTED', payload: { startUtc, endUtc, resourceId } });
      callbacks.onNewEventIntercepted?.(startUtc, endUtc, resourceId);
    },
    [callbacks, emit],
  );

  const queuedEvents = useMemo(
    () => events.filter((e) => e.queued === true),
    [events],
  );

  const handleScheduleQueuedEvent = useCallback(
    (eventId: string, newStartUtc: string, newEndUtc: string, resourceId?: string) => {
      let scheduled: ScheduleEvent | undefined;
      setEvents((prev) =>
        prev.map((e) => {
          if (e.id !== eventId) return e;
          scheduled = { ...e, queued: false, startUtc: newStartUtc, endUtc: newEndUtc, ...(resourceId ? { resourceId } : {}) };
          return scheduled;
        }),
      );
      if (scheduled) {
        callbacks.onQueuedEventScheduled?.(scheduled);
      }
    },
    [callbacks],
  );

  const value: ScheduleContextValue = {
    config: effectiveConfig,
    events,
    currentDate,
    setCurrentDate,
    setView,
    setTimezone,
    setSlotDuration,
    viewRange,
    callbacks,
    emit,
    handleDeleteEvent,
    handleSaveEvent,
    handleCreateEvent,
    setEvents,
    resourcePage,
    setResourcePage,
    holidayEvents,
    connectionStatus,
    queuedEvents,
    queueDragRef,
    handleScheduleQueuedEvent,
    selectedEvent,
    eventProperties,
    handleEventClick,
    handleEventDoubleClick,
    handleNewEventDoubleClick,
    suppressNextFormRef,
    pendingNewEventRequest,
    setPendingNewEventRequest,
    // ── Screener Gate ──────────────────────────────────────────────────────
    screenerGate,
    screenerState: screenerActions.screenerState,
    screenerPendingSlot: screenerActions.pendingSlot,
    screenerActiveScreener: screenerActions.activeScreener,
    screenerCrisisData: screenerActions.crisisData,
    handleSlotIntercepted: screenerActions.handleSlotIntercepted,
    screenerOnCompleted: screenerActions.onScreenerCompleted,
    screenerOnCrisisAcknowledged: screenerActions.onCrisisAcknowledged,
    screenerReset: screenerActions.reset,
    // ── Acuity Slot Reservation ────────────────────────────────────────────
    acuityReservations: acuitySlots.reservations,
    acuityMachineState: acuitySlots.machineState,
    handleAcuitySlotClaimed,
    applyAcuityConfig,
    privilegedView,
    // ── Provider Matching ────────────────────────────────────────────────────
    providerMatching,
    patientContext: livePatientContext,
    matchingHandle,
    // ── Waitlist + Auto-Backfill ───────────────────────────────────────────
    waitlistHandle,
    // ── No-Show Risk + Smart Reminder Engine ──────────────────────────────
    noShowRiskHandle,
    // ── Group Therapy ────────────────────────────────────────────────────────
    groupTherapyHandle,
  };

  return (
    <ScheduleContext.Provider value={value}>
      {children}
      <ScreenerGateDialogMount />
      <GroupEnrollDialogMount />
    </ScheduleContext.Provider>
  );
}
